// components/AddCustomerModal.tsx
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { customersAPI, licensesAPI } from '@/services/api';
import { toast } from 'sonner';
import LocationSelector from '@/components/LocationSelector'; // ← سِلکت مکان

interface AddCustomerModalProps {
    open: boolean;
    onClose: () => void;
    onAdded: () => void; // برای رفرش لیست در مودال انتخاب
}

const AddCustomerModal: React.FC<AddCustomerModalProps> = ({ open, onClose, onAdded }) => {
    const [form, setForm] = useState({
        first_name: '',
        last_name: '',
        phone_number: '',
        email: '',
        country_id: 'IR',  // مقدار پیش‌فرض
        province_id: '',
        city_id: '',
        address: '',
        complexname: '',
        complexnumber: '',
    });
    const [loading, setLoading] = useState(false);

    const handleChange = (k: string, v: string) => setForm((s) => ({ ...s, [k]: v }));

    const handleSubmit = async () => {
        if (!form.phone_number) {
            toast.error('شماره موبایل الزامی است');
            return;
        }
        setLoading(true);
        try {
            // 1) چک وجود شماره (سراسری)
            const check = await licensesAPI.checkBuyerByPhone(form.phone_number);
            if (check?.exists) {
                toast.error('این شماره قبلاً در سیستم ثبت شده است. لطفاً شماره دیگری وارد کنید.');
                setLoading(false);
                return;
            }

            // 2) افزودن مشتری برای همین ایجنت
            await customersAPI.add({
                firstName: form.first_name,
                lastName: form.last_name,
                phone: form.phone_number,
                email: form.email || undefined,
                countryId: form.country_id || 'IR',
                province: form.province_id || undefined,
                city: form.city_id || undefined,
                address: form.address || undefined,
                complexName: form.complexname || undefined,
                complexNumber: form.complexnumber || undefined,
            });

            toast.success('مشتری با موفقیت افزوده شد');
            onAdded();
        } catch (err: any) {
            toast.error(err?.message || 'خطا در افزودن مشتری');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent dir="rtl" className="max-w-lg max-h-[95vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>افزودن مشتری جدید</DialogTitle>
                </DialogHeader>

                <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label>نام</Label>
                            <Input
                                value={form.first_name}
                                onChange={(e) => handleChange('first_name', e.target.value)}
                            />
                        </div>
                        <div>
                            <Label>نام خانوادگی</Label>
                            <Input
                                value={form.last_name}
                                onChange={(e) => handleChange('last_name', e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label>موبایل</Label>
                            <Input
                                value={form.phone_number}
                                onChange={(e) => handleChange('phone_number', e.target.value)}
                                dir="ltr"
                                placeholder="0912xxxxxxx"
                            />
                        </div>
                        <div>
                            <Label>ایمیل (اختیاری)</Label>
                            <Input
                                value={form.email}
                                onChange={(e) => handleChange('email', e.target.value)}
                            />
                        </div>
                    </div>

                    {/* ✅ سِلکت کشور/استان/شهر */}
                    <LocationSelector
                        selectedCountry={form.country_id}
                        selectedProvince={form.province_id}
                        selectedCity={form.city_id}
                        onCountryChange={(v) => {
                            setForm((s) => ({ ...s, country_id: v, province_id: '', city_id: '' }));
                        }}
                        onProvinceChange={(v) => {
                            setForm((s) => ({ ...s, province_id: v, city_id: '' }));
                        }}
                        onCityChange={(v) => {
                            setForm((s) => ({ ...s, city_id: v }));
                        }}
                    />

                    <div>
                        <Label>آدرس</Label>
                        <Input
                            value={form.address}
                            onChange={(e) => handleChange('address', e.target.value)}
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label>نام مجتمع</Label>
                            <Input
                                value={form.complexname}
                                onChange={(e) => handleChange('complexname', e.target.value)}
                            />
                        </div>
                        <div>
                            <Label>شماره مجتمع</Label>
                            <Input
                                value={form.complexnumber}
                                onChange={(e) => handleChange('complexnumber', e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="flex gap-2 justify-end">
                        <Button variant="outline" onClick={onClose}>
                            انصراف
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={loading}
                            className="bg-green-600 hover:bg-green-700 text-white"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                                    در حال ثبت…
                                </>
                            ) : (
                                'ثبت مشتری'
                            )}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AddCustomerModal;
